/**
 * Rewards 页面 UI 层类型定义 - 匹配 mock 数据结构
 */

// Habit typeings
export type HabitType =
  | 'timeManagement'    // 时间管理
  | 'studyFocus'        // 学习专注
  | 'lifeCare'          // 生活自理
  | 'sportsHealth'      // 运动健康
  | 'responsibility'    // 责任担当
  | 'etiquette'         // 言行礼仪

// Rule base interface
export interface BaseRule {
  id: string;
  name: string;
  type: 'reward' | 'punishment';
  points: number;
  habits: HabitType[];
  description: string;
  triggerCount: number;
  yearTriggerCount: number;
  totalPoints: number;
  enabled: boolean;
  dailyCheckIn: boolean;
}

// Reward rule
export interface RewardRule extends BaseRule {
  type: 'reward';
  repeatPenalty: false;
  penaltyMultiplier: 1.0;
  timerEnabled?: boolean;
  deadlineHour?: number;
  deadlineMinute?: number;
  earlyBonus?: number;
  earlyBonusInterval?: number;
}

// Punishment rule
export interface PunishRule extends BaseRule {
  type: 'punishment';
  repeatPenalty: boolean;
  penaltyMultiplier: number;
}

export type Rule = RewardRule | PunishRule;

// Points Store Item
export interface PointsStoreItem {
  id: string;
  name: string;
  cost: number;
  category: string;
  icon: string;
  iconBg: string;
  description: string;
  redeemCount: number;
  enabled: boolean;
  hasCountdown?: boolean;
  countdownMinutes?: number;
}

// Habit labels and colors for UI
export const HABIT_LABELS: Record<HabitType, string> = {
  timeManagement: '时间管理',
  studyFocus: '学习专注',
  lifeCare: '生活自理',
  sportsHealth: '运动健康',
  responsibility: '责任担当',
  etiquette: '言行礼仪'
};

export const HABIT_COLORS: Record<HabitType, string> = {
  timeManagement: 'bg-blue-500',
  studyFocus: 'bg-violet-500',
  lifeCare: 'bg-emerald-500',
  sportsHealth: 'bg-amber-500',
  responsibility: 'bg-rose-500',
  etiquette: 'bg-teal-500'
};

// Category labels for store
export const CATEGORY_LABELS: Record<string, string> = {
  all: '全部',
  companionship: '亲子陪伴',
  activity: '户外活动',
  toy: '玩具礼物',
  food: '美食奖励',
  privilege: '特权解锁',
  book: '知识成长'
};

// Available icons for store items - 与APP端16个emoji图标完全一致
export const AVAILABLE_ICONS = [
  { icon: 'gift', emoji: '🎁', label: '礼物' },
  { icon: 'candy', emoji: '🍬', label: '糖果' },
  { icon: 'movie', emoji: '🎬', label: '电影' },
  { icon: 'game', emoji: '🎮', label: '游戏' },
  { icon: 'book', emoji: '📚', label: '书籍' },
  { icon: 'sports', emoji: '⚽', label: '足球' },
  { icon: 'music', emoji: '🎵', label: '音乐' },
  { icon: 'toy', emoji: '🧸', label: '玩具' },
  { icon: 'food', emoji: '🍰', label: '蛋糕' },
  { icon: 'travel', emoji: '🧭', label: '指南' },
  { icon: 'clock', emoji: '⏰', label: '闹钟' },
  { icon: 'camera', emoji: '📷', label: '拍照' },
  { icon: 'pen', emoji: '✏️', label: '画画' },
  { icon: 'star', emoji: '⭐', label: '星星' },
  { icon: 'crown', emoji: '👑', label: '皇冠' },
  { icon: 'trophy', emoji: '🏆', label: '奖杯' },
];

// emoji图标映射：value -> emoji字符，与APP端完全一致
export const ICON_EMOJI_MAP: Record<string, string> = {
  gift: '🎁', candy: '🍬', movie: '🎬', game: '🎮',
  book: '📚', sports: '⚽', music: '🎵', toy: '🧸',
  food: '🍰', travel: '🧭', clock: '⏰', camera: '📷',
  pen: '✏️', star: '⭐', crown: '👑', trophy: '🏆',
};

// Color presets for store item backgrounds
export const COLOR_PRESETS = [
  '#ef4444', '#f97316', '#f59e0b', '#10b981', '#3b82f6',
  '#8b5cf6', '#ec4899', '#0d9488', '#1a56db', '#dc2626'
];
